import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';

import { AnalyticsService } from '../../services/analytics.service';
import { ApiService } from '../../api/api.service';
import { AuthService } from '../../services/auth.service';
import { BehaviorSubject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRippleModule } from '@angular/material/core';
import { SnackbarService } from '../../services/snackbar.service';
import { SubscriptionInfo } from '../../models/subscription.model';
//import { environment } from '../../../environment/environment';

enum RenderingMode {
  loading,
  mobileActiveUser,
  webActiveUser,
  webCanceledStillActiveUser,
  expiredUser,
  firstTimeUser,
  hasFailingPayments,
}

@Component({
  selector: 'app-manage-account',
  standalone: true,
  imports: [
    MatDividerModule,
    MatChipsModule,
    MatCardModule,
    MatIconModule,
    MatRippleModule,
    MatButtonModule,
    MatProgressBarModule,
    CommonModule,
  ],
  templateUrl: './manage-account.component.html',
  styleUrl: './manage-account.component.scss',
})
export class ManageAccountComponent implements OnInit, OnDestroy {
  renderingMode$: BehaviorSubject<RenderingMode> =
    new BehaviorSubject<RenderingMode>(RenderingMode.loading);
  renderingModeEnum = RenderingMode;
  firstTimeContentHasLoaded$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  showSpinner$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  subscriptionDaysLeft: number = 0;
  renewalDate: string = '';
  previousEndDate: string = '';
  tokenPolling: any;
  activeWebSubPlanPriceOre: number = 0;
  activeWebSubPlanBillingInterval: 'year' | 'month' = 'year';
  shouldRenderChip: boolean = false;
  shouldSubscriptionStatusTextSayActive: boolean = false;
  shouldRenderSubscriptionStatusText: boolean = true;

  constructor(
    private api: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    public auth: AuthService,
    public snackService: SnackbarService,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.refreshUserToken();
    this.refreshSubscriptionData();

    // DEV ONLY: Set scenario by number (remove/comment out in production)
    /*if (!environment.production) {
      // Map: 0=loading, 1=mobileActiveUser, 2=webActiveUser, 3=webCanceledStillActiveUser, 4=expiredUser, 5=firstTimeUser, 6=hasFailingPayments
      const devScenario = Number(
        localStorage.getItem('devScenario') ?? 2
      ); // Default: webActiveUser
      this.renderingMode$.next(devScenario as RenderingMode);
      this.firstTimeContentHasLoaded$.next(true);
      // To change scenario, run in browser console: localStorage.setItem('devScenario', '4')
    }*/

    // Check URL parameter 'state' and fire event if 'complete'
    this.route.queryParams.subscribe((params) => {
      const state = params['state'];
      if (state === 'complete') {
        this.analyticsService.trackEvent(
          'purchase_completed', // Event Name
          'Purchase via Web', // Event Label
          'Purchase', // Event Category
          `User completed purchase via web` // Event Description (optional, no email for privacy)
        );
        console.log('Subscription completed event fired');
      }
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.tokenPolling);
  }

  refreshSubscriptionData() {
    this.api.getUserSubscriptionInfo().subscribe({
      next: (response) => {
        this.setSubscriptionData(response);
        this.firstTimeContentHasLoaded$.next(true);
      },
      error: (error) => {
        console.log('Error when getting subscription data!');
        this.firstTimeContentHasLoaded$.next(true);
        this.snackService.openSnackBar(
          'Vi stötte på ett problem. Vänligen försök igen om en stund.',
          5,
          'error'
        );
      },
    });
  }

  displayRoundedPrice(price: number) {
    return Math.round(price);
  }

  setSubscriptionData(subscription: SubscriptionInfo) {
    // DEV ONLY: Skip real data logic if dev scenario is set
    /*if (!environment.production && localStorage.getItem('devScenario') !== null) {
      return;
    }*/
    this.activeWebSubPlanPriceOre = subscription.activeWebSubPlanPriceOre;
    this.activeWebSubPlanBillingInterval =
      subscription.activeWebSubPlanBillingInterval;

    if (subscription.hasCurrentSub) {
      const now = new Date();
      const subscriptionEnd = this.parseApiDate(subscription.currentSubValidUntil);

      if (subscriptionEnd) {
        const diff = subscriptionEnd.getTime() - now.getTime();
        const days = Math.ceil(diff / (1000 * 3600 * 24));
        this.subscriptionDaysLeft = days;
        this.renewalDate = this.getMonth(subscriptionEnd);
      } else {
        this.subscriptionDaysLeft = 0;
        this.renewalDate = 'okänt datum';
      }
    }
    if (
      (subscription.hasBoughtAnySubFromWeb ||
        subscription.hasBoughtAnySubFromMobile) &&
      subscription.recentmostPlusExpiredAt
    ) {
      const previousSubscriptionEnd = this.parseApiDate(
        subscription.recentmostPlusExpiredAt
      );
      this.previousEndDate = previousSubscriptionEnd
        ? this.getMonth(previousSubscriptionEnd)
        : 'okänt datum';
    }

    switch (true) {
      //-- User has active web subscription:
      case subscription.hasActiveWebRenewingPaidSubscription &&
        !subscription.hasActiveMobilePaidSubscription &&
        !subscription.hasActiveWebCanceledPaidSubscription &&
        !subscription.hasFailingPaymentsRetrying:
        this.shouldRenderChip = true;
        this.shouldRenderSubscriptionStatusText = true;
        this.shouldSubscriptionStatusTextSayActive = true;
        this.renderingMode$.next(RenderingMode.webActiveUser);
        break;
      //-- EDGE CASE - User has subscription but there is no data for ActiveWebRenewingSubscription:
      case !subscription.hasActiveWebRenewingPaidSubscription &&
        !subscription.hasActiveWebCanceledPaidSubscription &&
        !subscription.hasActiveMobilePaidSubscription &&
        subscription.hasCurrentSub &&
        subscription.currentSubId != undefined &&
        subscription.currentSubId != null &&
        subscription.currentSubLevel == 'plus' &&
        !subscription.hasFailingPaymentsRetrying:
        this.shouldRenderChip = true;
        this.shouldRenderSubscriptionStatusText = true;
        this.shouldSubscriptionStatusTextSayActive = true;
        this.renderingMode$.next(RenderingMode.webActiveUser);
        break;
      //-- User has canceled their subscription:
      case subscription.hasActiveWebCanceledPaidSubscription &&
        !subscription.hasActiveWebRenewingPaidSubscription &&
        !subscription.hasActiveMobilePaidSubscription &&
        !subscription.hasFailingPaymentsRetrying:
        this.shouldRenderChip = true;
        this.shouldRenderSubscriptionStatusText = true;
        this.shouldSubscriptionStatusTextSayActive = true;
        this.renderingMode$.next(RenderingMode.webCanceledStillActiveUser);
        break;
      //-- User has subscription bought from mobile:
      case subscription.hasActiveMobilePaidSubscription &&
        !subscription.hasActiveWebRenewingPaidSubscription &&
        !subscription.hasActiveWebCanceledPaidSubscription &&
        !subscription.hasFailingPaymentsRetrying:
        this.shouldRenderChip = true;
        this.shouldRenderSubscriptionStatusText = true;
        this.shouldSubscriptionStatusTextSayActive = true;
        this.renderingMode$.next(RenderingMode.mobileActiveUser);
        break;
      //-- Subscription (mobile or web) has expired:
      case !subscription.hasActiveMobilePaidSubscription &&
        !subscription.hasActiveWebCanceledPaidSubscription &&
        !subscription.hasActiveWebRenewingPaidSubscription &&
        (subscription.hasBoughtAnySubFromMobile ||
          subscription.hasBoughtAnySubFromWeb) &&
        !subscription.hasFailingPaymentsRetrying:
        this.shouldRenderChip = true;
        this.shouldRenderSubscriptionStatusText = true;
        this.shouldSubscriptionStatusTextSayActive = false;
        this.renderingMode$.next(RenderingMode.expiredUser);
        break;
      //-- First time User:
      case !subscription.hasActiveMobilePaidSubscription &&
        !subscription.hasActiveWebCanceledPaidSubscription &&
        !subscription.hasActiveWebRenewingPaidSubscription &&
        !subscription.hasBoughtAnySubFromMobile &&
        !subscription.hasBoughtAnySubFromWeb &&
        !subscription.hasFailingPaymentsRetrying:
        this.shouldRenderChip = false;
        this.shouldRenderSubscriptionStatusText = true;
        this.shouldSubscriptionStatusTextSayActive = false;
        this.renderingMode$.next(RenderingMode.firstTimeUser);
        break;
      //-- User has web subscription but payment has failed:
      case subscription.hasFailingPaymentsRetrying:
        this.shouldRenderChip = true;
        this.shouldRenderSubscriptionStatusText = false;
        this.shouldSubscriptionStatusTextSayActive = true;
        this.renderingMode$.next(RenderingMode.hasFailingPayments);
        break;
      default:
        this.shouldRenderChip = false;
        this.renderingMode$.next(RenderingMode.loading);
        break;
    }

    console.log('What is the renderingMode? ', this.renderingMode$.value);
  }

  getMonth(date: Date): string {
    const months: string[] = [
      'januari',
      'februari',
      'mars',
      'april',
      'maj',
      'juni',
      'juli',
      'augusti',
      'september',
      'oktober',
      'november',
      'december',
    ];
    return (
      date.getDate() + ' ' + months[date.getMonth()] + ' ' + date.getFullYear()
    );
  }

  parseApiDate(dateValue: string | number | null | undefined): Date | null {
    if (dateValue === null || dateValue === undefined) {
      return null;
    }

    if (typeof dateValue === 'number') {
      if (dateValue <= 0) {
        return null;
      }
      const normalizedTimestamp = dateValue < 1_000_000_000_000 ? dateValue * 1000 : dateValue;
      const parsedDate = new Date(normalizedTimestamp);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const trimmedValue = dateValue.trim();
    if (!trimmedValue) {
      return null;
    }

    if (/^\d+$/.test(trimmedValue)) {
      const asNumber = Number(trimmedValue);
      if (!Number.isFinite(asNumber) || asNumber <= 0) {
        return null;
      }
      const normalizedTimestamp = asNumber < 1_000_000_000_000 ? asNumber * 1000 : asNumber;
      const parsedDate = new Date(normalizedTimestamp);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const parsedDate = new Date(trimmedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  refreshUserToken() {
    this.tokenPolling = setInterval(() => {
      console.log('Refreshing token...');
      this.api.refreshToken().subscribe({
        next: (reponse) => {
          this.refreshSubscriptionData();
        },
        error: (error) => {
          console.log('Fatal error! Could not refresh token!');
          clearInterval(this.tokenPolling);
          this.logOut();
        },
      });
    }, 180000);
  }

  onClickHandleManageAccount() {
    this.showSpinner$.next(true);
    this.api.sendUserPortalInitialization().subscribe({
      next: () => {
        console.log('Sending initialization to server successful');
      },
      error: () => {
        console.log('Error sending initialization to server');
      },
    });
    this.api.getManageSubscriptionLink().subscribe({
      next: (response) => {
        this.showSpinner$.next(false);
        window.open(response.url, '_self');
      },
      error: (error) => {
        this.showSpinner$.next(false);
        console.log('Error when fetching link for managing subscription!');
      },
      complete: () => {
        console.log('Get Manage Subscription Link request COMPLETE');
      },
    });
  }

  onClickRenewAccount() {
    this.router.navigate(['select-payment']);
  }

  onClickIOS() {
    window.open(
      'https://apps.apple.com/se/app/wrebit/id1046200849?platform=iphone'
    );
  }

  onClickAndroid() {
    window.open('https://play.google.com/store/apps/details?id=se.wrebit.app');
  }

  logOut() {
    localStorage.setItem('userToken', '');
    this.router.navigate(['login']);
  }
}
